/**
 * ClubMembersPage - Club Member Grid
 * 
 * Displays club members in a grid layout with avatars, names, and role badges.
 * Rebuilt to match Figma Nightfall Kit design with clean grid layout.
 * 
 * Reference: docs/design/FIGMA_IMPLEMENTATION_PLAN.md - Phase 7C
 */

import { useNavigate, useParams } from 'react-router-dom';
import { useClub } from '../hooks/useClubs';
import { useClubMembers } from '../hooks/useMembers';
import { useAuth } from '../hooks/useAuth';
import { TopBar, Avatar, RoleBadge, GridSkeleton, NoMembersEmptyState, EmptyState } from '../components';
import { createClubMembersMobileMenu } from '../components/MobileContextMenu';

export default function ClubMembersPage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const { logout } = useAuth();

  // Use React Query hooks
  const { data: clubData, isLoading: clubLoading, error: clubError } = useClub(slug || '');
  const { data: membersData, isLoading: membersLoading, error: membersError } = useClubMembers(slug || '');

  const isLoading = clubLoading || membersLoading;
  const error = clubError || membersError;

  // Check admin permission (leader or owner can view members)
  const isLeader = clubData?.membership?.role === 'leader' || clubData?.membership?.is_owner === true;
  const club = clubData?.club;
  const members = membersData || [];

  // Create mobile menu configuration
  const clubMembersMenu = slug ? createClubMembersMobileMenu(
    navigate,
    logout,
    slug,
    isLeader || false
  ) : undefined;

  if (isLoading) {
    return (
      <div className="flex flex-col bg-oro-bg">
        <TopBar 
          title="Members" 
          onBack={() => navigate(`/clubs/${slug}`)}
          mobileMenu={clubMembersMenu}
        />
        <div className="flex-1 overflow-y-auto">
          <div className="px-4 py-6">
            {/* Professional grid skeleton */}
            <GridSkeleton items={6} columns={2} />
          </div>
        </div>
      </div>
    );
  }

  if (error || !isLeader) {
    const errorMessage = error
      ? (error instanceof Error ? error.message : 'Unable to load members')
      : 'Admin access is needed to view members';
      
    return (
      <div className="flex flex-col bg-oro-bg">
        <TopBar 
          title="Members" 
          onBack={() => navigate(`/clubs/${slug}`)}
          mobileMenu={clubMembersMenu}
        />
        <div className="flex-1 overflow-y-auto">
          <div className="px-4 py-12">
            <EmptyState
              icon="⚠️"
              title="Cannot load members"
              message={errorMessage}
              actionLabel="Back to Club"
              onAction={() => navigate(`/clubs/${slug}`)}
            />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col bg-oro-bg">
      <TopBar 
        title="Members" 
        onBack={() => navigate(`/clubs/${slug}`)}
        mobileMenu={clubMembersMenu}
      />
      
      <div className="flex-1 overflow-y-auto">
        <div className="px-4 py-6 space-y-6">
          {/* Header */}
          <div className="text-center space-y-1">
            <p className="text-xs uppercase tracking-[0.35em] text-text-muted">
              {club?.name}
            </p>
            <p className="text-text-secondary">
              {members.length} {members.length === 1 ? 'member' : 'members'}
            </p>
          </div>

          {/* Empty State */}
          {members.length === 0 && (
            <div className="py-8">
              <NoMembersEmptyState 
                variant="default"
                onAction={() => navigate(`/clubs/${slug}/invite`)}
              />
            </div>
          )}

          {/* Member Grid */}
          {members.length > 0 && (
            <div className="grid grid-cols-2 gap-4">
              {members.map((member) => (
                <div
                  key={member.user_id}
                  className="bg-oro-surface border border-oro-border rounded-2xl p-4 flex flex-col items-center text-center hover:border-accent/30 transition-colors shadow-card"
                >
                  {/* Avatar */}
                  <Avatar
                    name={member.public_display_name}
                    profile_image_url={member.profile_image_url}
                    user_level={member.user_level as 'free' | 'founder'}
                    size="lg"
                    showGoldRing={true}
                    className="mb-3"
                  />
                  
                  {/* Name */}
                  <h3 className="text-sm font-semibold text-text-primary mb-1 line-clamp-1 w-full">
                    {member.public_display_name}
                  </h3>
                  
                  {/* Role Badge */}
                  <RoleBadge role={member.role} size="xs" />
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
